import Box from 'UIKit/Box/Box'
import { FlexProps } from 'UIKit/Box/types'
import Image from 'UIKit/Image'
import Link from 'UIKit/Link/Link'
import CircleLoader from 'UIKit/Loader/CircleLoader'
import Text from 'UIKit/Text'
import { GameDetailsOnBoardModeEnum } from 'config/constants'
import { RouteConfig } from 'config/constants/route'
import { Game, GamePlayAction, GameSession } from 'config/types/game'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import { useCloseAllModal } from 'hooks/useModal'
import { useRestrictedGamesC2PBonus } from 'hooks/useRestrictedGamesC2PBonus'
import { useIsTelegramOrigin } from 'hooks/useTelegram'
import { RowCenter } from 'layout/Components/Row'
import React, { useState } from 'react'
import { Trans } from 'react-i18next'
import { BaseResponse } from 'services/types'
import { useClientIpInfo, useProviderInfo } from 'state/app/hooks'
import { useFeaturedGame } from 'state/profile/hooks'
import styled, { keyframes } from 'styled-components'
import { formatRoute } from 'utils/stringHelper'
import GameItemActions, { GameItemActionsProps } from './GameItemActions'
import NotAvailableGameItemOverlay from './NotAvailableGameItemOverlay'

interface GameItemProps {
  game: Game
  large?: boolean
  gamePlayAction?: GamePlayAction
  ActionComponent?: React.FC<GameItemActionsProps>
  handlePlayFn?: (game: Game) => void
  handleFetchGame?: (game: Game) => Promise<BaseResponse<GameSession>>
}

const GameItem: React.FC<GameItemProps & FlexProps> = ({
  game,
  gamePlayAction = GamePlayAction.Play,
  handlePlayFn,
  handleFetchGame,
  large,
  ActionComponent,
  ...props
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const closeAllModal = useCloseAllModal()
  const {
    featuredGameInfo: { isWageringBonusGame },
  } = useFeaturedGame(game.code)

  const [favoriteCount, setFavoriteCount] = useState(game.favoriteCount)
  const provider = useProviderInfo(game.providerCode)
  const { country } = useClientIpInfo()
  const isBlocked = provider?.blacklistCountry?.includes(country)
  const isRestrictedGame = useRestrictedGamesC2PBonus(game.code)

  const isFreeSpinBonusGame = gamePlayAction === GamePlayAction.MakeSpinWithSelectedCurrency
  const { isMobile } = useMatchBreakpoints()
  const isApplyAutoDirectProviderGameSession = isFreeSpinBonusGame && isMobile
  const isTelegram = useIsTelegramOrigin()

  const [isLoadingFreeSpinGame, setIsLoadingFreeSpinGame] = useState(false)

  return (
    <Link
      disabled={isBlocked || isRestrictedGame || game.isMaintenance || isApplyAutoDirectProviderGameSession}
      aria-label={game.name}
      id="game-item"
      style={isBlocked || isRestrictedGame || game.isMaintenance ? { pointerEvents: 'none' } : {}}
      onMouseOver={() => setIsHovered(true)}
      onMouseOut={() => setIsHovered(false)}
      href={`${formatRoute(RouteConfig.GameDetail, { slug: game.slug })}${
        gamePlayAction === GamePlayAction.MakeSpinWithSelectedCurrency
          ? `?onBoardType=${GameDetailsOnBoardModeEnum.PlayFreeSpinWithSelectedCurrency}`
          : gamePlayAction === GamePlayAction.MakeSpin
          ? `?onBoardType=${GameDetailsOnBoardModeEnum.PlayFreeSpin}`
          : ''
      }`}
      disabledDefaultStyle
      onClick={async () => {
        if (isMobile && isFreeSpinBonusGame) {
          setIsLoadingFreeSpinGame(true)
          const response = await handleFetchGame(game)
          if (response) {
            if (!isTelegram) closeAllModal()
          }
          setIsLoadingFreeSpinGame(false)
        } else if (handlePlayFn) {
          handlePlayFn(game)
          closeAllModal()
        }
      }}
    >
      <StyledWrapper {...props}>
        <StyledImgWrapper position="relative">
          {(isBlocked || isRestrictedGame || game.isMaintenance) && (
            <NotAvailableGameItemOverlay
              isBlocked={isBlocked}
              isRestricted={isRestrictedGame}
              iconSize={40}
              alignItems="flex-end !important"
              fontSize="14px"
              color="textTertiary"
              pb="10px"
            />
          )}
          {isWageringBonusGame ? (
            <StyledBonusBadge>
              <Text fontSize="max(12px,0.5vw)" fontWeight={500} lineHeight="134%">
                <Trans>Bonus Wager</Trans>
              </Text>
            </StyledBonusBadge>
          ) : null}
          <Image width={large ? 800 : 350} height={large ? 800 : 350} src={game.thumbnail} alt={game.name} blurLoader />

          {isMobile && game.rtp && (
            <Box position="absolute" top="0px" left="0px" bottom="0px" right="0px" zIndex={1}>
              <Text
                fontSize="12px"
                fontWeight={800}
                letterSpacing="-0.24x"
                style={{ textShadow: '1px 1px 1px rgba(0, 0, 0, 0.54)' }}
                textAlign="left"
                p="8px"
              >
                RTP {game.rtp}
              </Text>
            </Box>
          )}

          <StyledHoverWrapper>
            {isHovered ? (
              ActionComponent ? (
                <ActionComponent
                  game={game}
                  defaultFavoriteCount={favoriteCount}
                  onFavoriteCountUpdated={(favoriteCount) => setFavoriteCount(favoriteCount)}
                />
              ) : (
                <Box position="absolute" top="0px" left="0px" bottom="0px" right="0px">
                  {!game.isMaintenance && game.rtp && (
                    <Text
                      fontSize="16px"
                      fontWeight={800}
                      letterSpacing="-0.32px"
                      style={{ textShadow: '1px 1px 1px rgba(0, 0, 0, 0.54)' }}
                      textAlign="left"
                      p="12px"
                    >
                      RTP {game.rtp}
                    </Text>
                  )}
                  <GameItemActions
                    game={game}
                    defaultFavoriteCount={favoriteCount}
                    onFavoriteCountUpdated={(favoriteCount) => setFavoriteCount(favoriteCount)}
                  />
                </Box>
              )
            ) : null}
          </StyledHoverWrapper>
          {isLoadingFreeSpinGame && (
            <StyledFreeSpinLoadingWrapper>
              <CircleLoader />
            </StyledFreeSpinLoadingWrapper>
          )}
        </StyledImgWrapper>
      </StyledWrapper>
    </Link>
  )
}

const StyledBonusBadge = styled(Text)`
  font-size: 12px;
  font-weight: 500;
  position: absolute;
  padding: 4px 8px;

  background: linear-gradient(223deg, #7f00ff 0%, rgba(225, 0, 255, 0.8) 100%);
  top: 0;
  right: 0;

  border-top-right-radius: 8px;
  border-bottom-left-radius: 8px;
  backdrop-filter: blur(6px);

  z-index: 1;
`

const StyledHoverWrapper = styled(RowCenter)`
  z-index: 1;
  top: 0px;
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  background: linear-gradient(180deg, rgba(22, 28, 36, 0) -22.78%, #191d25e0 55%, #17181b 86.75%);
`

const StyledFreeSpinLoadingWrapper = styled(StyledHoverWrapper)`
  opacity: 1;
`

const grow = keyframes`
  0% {
    bottom: -10px;
  }
  100% {
    bottom: 0;
  };
`

const StyledWrapInfoGame = styled(Box)`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
`

export const StyledWrapper = styled(Box)`
  width: 100%;
  padding: 12px;
  cursor: pointer;
  position: relative;

  scroll-snap-align: start;
  scroll-snap-stop: normal;

  img {
    transition: ${({ theme }) => theme.transitions.medium};
  }

  ${({ theme }) => theme.mediaQueries.md} {
    &:hover {
      ${StyledHoverWrapper} {
        opacity: 1;
        ${StyledWrapInfoGame} {
          animation-name: ${grow};
          animation-duration: 300ms;
        }
      }
    }
  }
`

const StyledImgWrapper = styled(Box)`
  overflow: hidden;
  border-radius: ${({ theme }) => theme.radii.small};
`

export default GameItem
